import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from "yup"
import { httpsCallable } from "firebase/functions";
import Grid from '@mui/material/Unstable_Grid2/Grid2'
import { useDispatch, useSelector } from 'react-redux'
import { functions } from "../../../Infrastructure/config";
import { StepsHeader } from '../../Common/StepsHeader/stepsHeader'
import { ShowErrorToast } from '../../Common/Toast/toast'
import { basicsStepSx, ChoiceTypo, ErrorBlockSmall, QuestionName } from '../styles'
import { Box, FormControl, FormControlLabel, InputAdornment, Radio, RadioGroup } from '@mui/material'
import { basicStepControl, resetBasicStepValues, incrementCoursesSteps } from '../../../Infrastructure/States/coursesStepsSlice'
import { StepsFooter } from '../../Common/StepsFooter/stepsFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '../../Common/Loader/loader';
import { cleanCourseState, setCourseDetails } from '../../../Infrastructure/States/courseDetailsSlice';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import AttachMoney from '@mui/icons-material/AttachMoney';

import { COURSE_CATEGORIES } from '../../../Infrastructure/Constants/courseCategories';
import {
  StyledOutlinedTextField,
} from '../../../Infrastructure/Theme/styledComponents';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import paths from '../../../Infrastructure/Router/paths';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';

export const BasicsStep = () => {
    const costRef = useRef(null);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const errorRef = useRef(null);
    const formikRef = useRef(null);
    const location = useLocation();
    const { pathname } = useLocation()
    const ip = useSelector((state) => state.ip.address)
    const page = useSelector((state)=> state.route.currentRoute)
    const [submitCount, setSubmitCount] = useState(0);
    const { user, userId } = useAuthentication();
    const uid = user?.uid;
    const courseId = location.state ? location.state : null;
    const [isLoading, setIsLoading] = useState(false);
    const userData = useSelector((state) => state.userData.data)
    const course = useSelector((state) => state.course.courseDetails)
    const courseSteps = useSelector((state) => state.courseSteps.courseSteps)
    const basicStepState = useSelector((state) => state.courseSteps.basicStepState)
    const courseCost = course?.course?.courseDetails?.basics?.cost;
    const courseTitle = course?.course?.courseDetails?.basics?.title;
    const courseSubTitle = course?.course?.courseDetails?.basics?.subTitle;
    const categoryValue = course?.course?.courseDetails?.basics?.category;
    const handleExit = async () => {
        try {
            setIsLoading(true)
            if (course?.courseId) {
                const updateCategoryStep = httpsCallable(functions, "updatecategorystep");
                await updateCategoryStep({ categoryValue: basicStepState?.categoryValue, courseTitle: formik.values.courseTitle, courseSubTitle: formik.values.courseSubTitle, cost: formik.values.courseCost, status: "in_progress", courseId: course?.courseId, step: 1, ip, page });
            }
            navigate(paths.dashboardPage.path);
        } catch (error) {
            navigate(paths.dashboardPage.path);
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.BASICS_STEP_UPDATE_FAILED,
              uid: userId || null,
              source: "basicsStep.jsx",
              emailInfo: null,
              metadata: { courseId: course?.courseId, step: 1 },
              error: error?.message || null
            });
            ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST);
        }
        finally {
            setIsLoading(false)
            formik.resetForm();
            dispatch(resetBasicStepValues());
            dispatch(cleanCourseState());
        }
    }
    const formik = useFormik({
        initialValues: { courseTitle: courseTitle || "", courseSubTitle: courseSubTitle || "", courseCost: course?.course?.courseDetails?.basics?.cost || "" },
        validationSchema: Yup.object().shape({
            courseTitle: Yup.string()
            .matches(/^[A-Za-z0-9 ]+$/, "Only letters & numbers are allowed")
            .required("Course Title Required"),
            courseCost: Yup.number()
            .typeError("Enter a valid amount")
            .required("Course Cost Required")
            .min(1, "Price must be greater than 0")
            .max(999999999999, "Maximum allowed price is 999,999,999,999")
        }),
        validateOnBlur: false, 
        validateOnChange: false,
        onSubmit: async (values) => {
            if (courseSteps >= 1 && courseSteps <= 6) {
                dispatch(basicStepControl({
                    courseCost: values.courseCost,
                    courseTitle: values.courseTitle,
                    question: "courseTitle",
                    courseSubTitle: values.courseSubTitle
                }));

                try {
                    setIsLoading(true);
                    const cleanTitle = values.courseTitle.replace(/\s+/g, " ").trim();
                    const updateCategoryStep = httpsCallable(functions, "updatecategorystep");
                    const res = await updateCategoryStep({
                        categoryValue: basicStepState?.categoryValue,
                        courseTitle: cleanTitle,
                        courseSubTitle: values.courseSubTitle,
                        cost: values.courseCost,
                        status: "in_progress",
                        courseId: course?.courseId,
                        ip,
                        page
                    });
                    // Check if the response indicates a failure
                    if (!res?.data?.isSuccess) {
                        // Set the error message from the response
                        formik.setFieldError("courseTitle", res.data.errorMessage || "An error occurred.");
                        await safeClientLogEvent({
                          ...USER_LOGS_CLIENT.BASICS_STEP_UPDATE_FAILED,
                          uid: userId || null,
                          source: "basicsStep.jsx",
                          emailInfo: null,
                          metadata: { courseId: course?.courseId, errorMessage: res.data.errorMessage || "An error occurred." },
                          error: res.data.errorMessage || "An error occurred."
                        });
                        return; // Exit the function to prevent navigation to the next step
                    }
                    await safeClientLogEvent({
                      ...USER_LOGS_CLIENT.BASICS_STEP_UPDATE_SUCCESS,
                      uid: userId || null,
                      source: "basicsStep.jsx",
                      emailInfo: null,
                      metadata: { courseId: course?.courseId, courseTitle: cleanTitle, courseSubTitle: values.courseSubTitle, cost: values.courseCost },
                      error: null
                    });
                    const createdCourseId = res?.data?.courseId;
                    if (course?.courseId) {
                        const newCourse = userData.educator.courses[createdCourseId];
                        dispatch(setCourseDetails({ course: newCourse, courseId: createdCourseId }));
                    } else {
                        dispatch(setCourseDetails({ courseId: createdCourseId }));
                    }
                    dispatch(incrementCoursesSteps());
                } catch (error) {
                    // Fallback error handling if an exception is thrown
                    if (error.message.includes("Course title already exists")) {
                        formik.setFieldError("courseTitle", "This course title is already taken. Please choose another one.");
                    } else {
                        ShowErrorToast(error);
                    }
                    await safeClientLogEvent({
                      ...USER_LOGS_CLIENT.BASICS_STEP_UPDATE_FAILED,
                      uid: userId || null,
                      source: "basicsStep.jsx",
                      emailInfo: null,
                      metadata: { courseId: course?.courseId },
                      error: error?.message || null
                    });
                    ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST);
                } finally {
                    setIsLoading(false);
                }
            }
        }
    })
    formikRef.current = formik;
    const handleOptionChange = (e) => {
        let categoryValue = e.target.value;
        dispatch(basicStepControl({ categoryValue, question: "category" }));
    };
    useEffect(() => {
        if (submitCount === 0) return
        if (formik.errors.courseTitle) {
            requestAnimationFrame(() => {
                if (errorRef.current) {
                    errorRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            });
            return
        }
        if (formik.errors.courseCost) {
            requestAnimationFrame(() => {
                if (costRef.current) {
                    costRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            });
        }
    }, [submitCount, formik.errors])
    useEffect(() => {
        if (userData && categoryValue) {
            dispatch(basicStepControl({ categoryValue: categoryValue, question: "category" }));
        }
        if (userData && courseCost) {
            dispatch(basicStepControl({ courseTitle: courseCost, question: "courseCost" }));
            formik.setFieldValue("courseCost", courseCost || '');
        }
        if (userData && courseTitle) {
            dispatch(basicStepControl({ courseTitle: courseTitle, question: "courseTitle" }));
            formik.setFieldValue("courseTitle", courseTitle || '');
        }
        if (userData && courseSubTitle) {
            dispatch(basicStepControl({ courseSubTitle: courseSubTitle, question: "courseTitle" }));
            formik.setFieldValue("courseSubTitle", courseSubTitle || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, dispatch, categoryValue, courseTitle, courseSubTitle, courseCost])
    useEffect(() => {

        if (courseId && userData && userData.educator && userData.educator.courses) {
            const course = userData.educator.courses[courseId ? courseId : ''];
            if (course) {
                dispatch(setCourseDetails({ course, courseId, educator_id: uid }));
            }
        }
        else if (course?.courseId && userData && userData.educator && userData.educator.courses) {
            let newCourseId = course?.courseId;
            const courseData = userData.educator?.courses[newCourseId ? newCourseId : ''];
            if (courseData) {
                dispatch(setCourseDetails({ course, courseId: newCourseId, educator_id: uid }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleContinueClick = async () => {
        setSubmitCount(prev => prev + 1)
        formikRef.current.setTouched({
            courseTitle: true,
            courseCost: true,
            courseSubTitle: true
        })
        if (formikRef.current) {
            await formikRef.current.handleSubmit();
        }
        if (!userData || !course || !course?.course?.courseDetails) {
            return <Box height={'80vh'}><Loader /></Box>;
        }
    };
    useEffect(() => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
    }, [pathname])

    return (
        <Box sx={basicsStepSx.root}>
            <Box sx={basicsStepSx.header}>
                <StepsHeader steps={courseSteps} handleExit={handleExit} />
            </Box>
            <Box
                component="form"
                onSubmit={formik.handleSubmit}
                sx={basicsStepSx.form}
            >
                {
                    isLoading ? <Loader/> :
                        <Box sx={basicsStepSx.content}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={12} md={12} lg={6} xl={6} width="100%">
                                    <QuestionName>
                                        What will be the title of your course?
                                    </QuestionName>
                                    <StyledOutlinedTextField
                                        name="courseTitle"
                                        label={
                                            formik.touched.courseTitle && Boolean(formik.errors.courseTitle)
                                                ? formik.errors.courseTitle
                                                : "Course Title"
                                        }
                                        error={formik.touched.courseTitle && Boolean(formik.errors.courseTitle)}
                                        variant="outlined"
                                        placeholder="Course Title"
                                        onChange={formik.handleChange}
                                        value={formik.values.courseTitle}
                                        ml={1}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid xs={12} sm={12} md={12} lg={6} xl={6} width="100%">
                                    <QuestionName>
                                        What will be the cost of your course?
                                    </QuestionName>
                                    <StyledOutlinedTextField
                                        type='number'
                                        name="courseCost"
                                        inputRef={costRef}
                                        label={
                                            formik.touched.courseCost && Boolean(formik.errors.courseCost)
                                                ? formik.errors.courseCost
                                                : "Course Cost"
                                        }
                                        error={formik.touched.courseCost && Boolean(formik.errors.courseCost)}
                                        variant="outlined"
                                        placeholder="Course Cost"
                                        onChange={formik.handleChange}
                                        value={formik.values.courseCost}
                                        ml={1}
                                        InputProps={{
                                            inputMode: "numeric",
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AttachMoney fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            onKeyPress: (event) => {
                                                if (isNaN(event.key) || event.key === ' ') {
                                                    event.preventDefault();
                                                }
                                            },
                                            onKeyDown: (e) => {
                                                if (e.key === '-' || e.key === 'Minus') {
                                                    e.preventDefault();
                                                }
                                            },
                                            onPaste: (e) => {
                                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                                if (!/^\d+$/.test(paste)) {
                                                    e.preventDefault();
                                                }
                                            },

                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid xs={12} sm={12} md={12} lg={6} xl={6} width="100%">
                                    <QuestionName>
                                        What will be the Subtitle of your course?
                                    </QuestionName>
                                    <StyledOutlinedTextField
                                        name="courseSubTitle"
                                        label={
                                            formik.touched.courseSubTitle && Boolean(formik.errors.courseSubTitle)
                                                ? formik.errors.courseSubTitle
                                                : "Course Subtitle (optional)"
                                        }
                                        error={formik.touched.courseSubTitle && Boolean(formik.errors.courseSubTitle)}
                                        variant="outlined"
                                        placeholder="Course Subtitle"
                                        onChange={formik.handleChange}
                                        value={formik.values.courseSubTitle}
                                        ml={1}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <Box mt={4}>
                                <QuestionName variant="h6">
                                    What category does your course best fit in?
                                </QuestionName>
                            <Grid container spacing={{ xs: 0, md: 3 }}>
                                {[0, 8, 16].map(start => (
                                    <Grid key={start} xs={12} md={6} lg={4} xl={4}>
                                        <FormControl fullWidth>
                                            <RadioGroup onChange={handleOptionChange} value={basicStepState.categoryValue}>
                                                {COURSE_CATEGORIES.slice(start, start + 8).map(option => (
                                                    <FormControlLabel
                                                        key={option.name}
                                                        value={option.label}
                                                        control={<Radio
                                                                size="medium"
                                                                sx={basicsStepSx.radioIcon}
                                                            />
                                                        }
                                                        label={<ChoiceTypo>{option.label}</ChoiceTypo>}
                                                        sx={basicsStepSx.radioItem}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                }
                <ErrorBlockSmall ref={errorRef} sx={basicsStepSx.error}>
                    {formik.errors.courseTitle}
                </ErrorBlockSmall>
            </Box>
            <Box sx={basicsStepSx.footer}>
               <StepsFooter handleContinueClick={handleContinueClick} error={formik.errors.courseTitle} isLoading={isLoading} />
            </Box>
        </Box>
    )
}
